import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import styled from "styled-components";
import Arrow from "assets/smartAnI.svg";
import ArrowDr from "assets/smartAnIDr.svg";
import moment from "moment";
import WeeksMenu from "./components/WeeksMenu";
import MonthsMenu from "./components/MonthsMenu";
import YearsMenu from "./components/YearsMenu";
import CustomDateRange from "./components/CustomDateRange";
import {detectCategoryFromLabel, getDateRangeFromPreset} from "./utils/datePresets";
import {DEFAULT_STYLE_CONFIG} from "components/DateRangeDropdown/styles";
import {DateRangeDropdownProps} from "components/DateRangeDropdown/types";

const PLACEHOLDER_TEXT = "Select a period";

const DateRangeDropdown: React.FC<DateRangeDropdownProps> = (props) => {
    const {
        open,
        setOpen,
        setNewData,
        selectedData,
        setSelectedData,
        periodRangeRef,
        styleConfig = {},
    } = props;

    // Merge custom styles with defaults
    const mergedStyles = {
        leftPane: { ...DEFAULT_STYLE_CONFIG.leftPane, ...styleConfig.leftPane },
        rightPane: { ...DEFAULT_STYLE_CONFIG.rightPane, ...styleConfig.rightPane },
        menuItem: { ...DEFAULT_STYLE_CONFIG.menuItem, ...styleConfig.menuItem },
        dropdown: { ...DEFAULT_STYLE_CONFIG.dropdown, ...styleConfig.dropdown },
        footer: { ...DEFAULT_STYLE_CONFIG.footer, ...styleConfig.footer },
    };

    const [submenu, setSubmenu] = useState<string | null>(null);
    const [tempSelectedData, setTempSelectedData] = useState<string>(PLACEHOLDER_TEXT);
    const [customDatesValid, setCustomDatesValid] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [isPositioned, setIsPositioned] = useState(false);

    const formRef = useRef<any>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            if (selectedData === PLACEHOLDER_TEXT) {
                setSubmenu(null);
                setTempSelectedData(PLACEHOLDER_TEXT);
            } else {
                const category = detectCategoryFromLabel(selectedData);
                setSubmenu(category);
                setTempSelectedData(selectedData);
            }

            if (selectedData !== PLACEHOLDER_TEXT) {
                const category = detectCategoryFromLabel(selectedData);
                if (category === "Custom") {
                    setCustomDatesValid(true);
                } else {
                    setCustomDatesValid(false);
                }
            } else {
                setCustomDatesValid(false);
            }
        }
    }, [open, selectedData]);

    useLayoutEffect(() => {
        if (!open || !dropdownRef.current || !periodRangeRef?.current) return;

        setIsPositioned(false);

        const dropdown = dropdownRef.current;
        const trigger = periodRangeRef.current;

        const triggerRect = trigger.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const GAP = 12;

        let left = triggerRect.left;
        if (left + dropdownRect.width > viewportWidth - GAP) {
            left = viewportWidth - dropdownRect.width - GAP;
        }
        if (left < GAP) left = GAP;

        let top = triggerRect.bottom + 8;
        if (top + dropdownRect.height > viewportHeight - GAP) {
            top = triggerRect.top - dropdownRect.height - 8;
        }

        setDropdownStyle({ left, top });
        setIsPositioned(true);
    }, [open, submenu, periodRangeRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (periodRangeRef?.current?.contains(target)) {
                return;
            }

            if (dropdownRef.current && !dropdownRef.current.contains(target)) {
                const isDatePickerClick =
                    target.closest(".p-datepicker") ||
                    target.closest(".p-calendar") ||
                    target.closest(".p-calendar-panel");

                if (!isDatePickerClick) {
                    setTempSelectedData(selectedData);
                    setOpen(false);
                }
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, setOpen, selectedData, periodRangeRef]);

    const onSubmit = async (values: any) => {
        try {
            if (values.from && values.to) {
                const fromDate = moment(values.from).toDate();
                const toDate = moment(values.to).toDate();

                const dateString = `${moment(values.from).format("YYYY-MM-DD")} - ${moment(values.to).format("YYYY-MM-DD")}`;
                setTempSelectedData(dateString);
                setSelectedData(dateString);
                setNewData([fromDate, toDate]);
                setCustomDatesValid(true);
                setOpen(false);
            }
        } catch (err) {
            console.error("Date selection failed:", err);
        }
    };

    const handleSave = async () => {
        if (submenu === "Custom" && formRef.current) {
            formRef.current();
        } else {
            if (tempSelectedData === PLACEHOLDER_TEXT) {
                return;
            }
            const dateRange = getDateRangeFromPreset(tempSelectedData);
            setSelectedData(tempSelectedData);
            setNewData(dateRange);
            setOpen(false);
        }
    };

    const handleClear = () => {
        setSelectedData(PLACEHOLDER_TEXT);
        setTempSelectedData(PLACEHOLDER_TEXT);
        setSubmenu(null);
        setCustomDatesValid(false);
        setNewData([]);
        setOpen(false);
    };

    const handlePresetSelect = (label: string) => {
        setTempSelectedData(label);
        setCustomDatesValid(false);
    };

    const handleMenuItemClick = (item: string) => {
        setSubmenu(item);

        if (tempSelectedData !== PLACEHOLDER_TEXT) {
            const currentCategory = detectCategoryFromLabel(tempSelectedData);
            if (currentCategory !== item) {
                setTempSelectedData(PLACEHOLDER_TEXT);
            }
        }
    };

    const renderSubmenuContent = () => {
        switch (submenu) {
            case "Weeks":
                return (
                    <WeeksMenu
                        selectedData={tempSelectedData}
                        setSelectedData={handlePresetSelect}
                        menuItemStyle={mergedStyles.menuItem}
                    />
                );
            case "Months":
                return (
                    <MonthsMenu
                        selectedData={tempSelectedData}
                        setSelectedData={handlePresetSelect}
                        menuItemStyle={mergedStyles.menuItem}
                    />
                );
            case "Years":
                return (
                    <YearsMenu
                        selectedData={tempSelectedData}
                        setSelectedData={handlePresetSelect}
                        menuItemStyle={mergedStyles.menuItem}
                    />
                );
            case "Custom":
                return (
                    <CustomDateRange
                        selectedData={tempSelectedData}
                        formRef={formRef}
                        onSubmit={onSubmit}
                        onDatesChange={setCustomDatesValid}
                    />
                );
            default:
                return null;
        }
    };

    return (
        open && (
            <Wrapper className="wrapper">
                <Dropdown
                    ref={dropdownRef}
                    $borderRadius={mergedStyles.dropdown.borderRadius}
                    $maxWidth={mergedStyles.dropdown.maxWidth}
                    style={{
                        ...dropdownStyle,
                        opacity: isPositioned ? 1 : 0,
                        transition: "opacity 0.12s ease",
                    }}
                >
                    <div style={{ display: "flex" }}>
                        <LeftPane
                            $minWidth={mergedStyles.leftPane.minWidth}
                            $width={mergedStyles.leftPane.width}
                            $padding={mergedStyles.leftPane.padding}
                        >
                            <SectionTitle>Period Range</SectionTitle>
                            {["Weeks", "Months", "Years", "Custom"].map((item) => (
                                <MenuItem
                                    key={item}
                                    onClick={() => handleMenuItemClick(item)}
                                    $width={mergedStyles.menuItem.width}
                                    $height={mergedStyles.menuItem.height}
                                    $fontSize={mergedStyles.menuItem.fontSize}
                                    $padding={mergedStyles.menuItem.padding}
                                    style={{
                                        color: submenu === item ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.5)",
                                        background: submenu === item ? "rgba(226,229,255,1)" : "",
                                    }}
                                >
                                    {item} {submenu === item ? <ArrowDr /> : <Arrow />}
                                </MenuItem>
                            ))}
                        </LeftPane>

                        {submenu && (
                            <RightPane
                                $width={mergedStyles.rightPane.width}
                                $padding={mergedStyles.rightPane.padding}
                            >
                                {renderSubmenuContent()}
                            </RightPane>
                        )}
                    </div>
                    <Footer $padding={mergedStyles.footer.padding} $gap={mergedStyles.footer.gap}>
                        <ClearButton onClick={handleClear}>Clear</ClearButton>
                        <SaveButton
                            onClick={handleSave}
                            disabled={
                                submenu === "Custom"
                                    ? !customDatesValid
                                    : tempSelectedData === PLACEHOLDER_TEXT
                            }
                        >
                            Save
                        </SaveButton>
                    </Footer>
                </Dropdown>
            </Wrapper>
        )
    );
};

export default DateRangeDropdown;

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
    font-family: "SF Pro Display", sans-serif;
`;

const Dropdown = styled.div<{ $borderRadius?: string; $maxWidth?: string }>`
    position: fixed;
    background: #fff;
    border-radius: ${(props) => props.$borderRadius || "10px"};
    box-shadow: 0px 0px 10px 3px rgba(188, 188, 191, 0.68);
    z-index: 1000;
    width: max-content;
    max-width: ${(props) => props.$maxWidth || "calc(100vw - 24px)"};
`;

const LeftPane = styled.div<{ $minWidth?: string; $width?: string; $padding?: string }>`
    width: ${(props) => props.$width || "max-content"};
    min-width: ${(props) => props.$minWidth || "192px"};
    padding: ${(props) => props.$padding || "0 10px 0 10px"};
    border-right: 1px solid #f3f3f3;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (max-width: 480px) {
        min-width: 160px;
    }
`;

const RightPane = styled.div<{ $width?: string; $padding?: string }>`
    width: ${(props) => props.$width || "200px"};
    padding: ${(props) => props.$padding || "28px 10px 10px 10px"};
    animation: slideIn 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (max-width: 760px) {
        padding-top: 28px;
        width: 100%;
    }

    @media (max-width: 480px) {
        padding-left: 0;
    }

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateX(8px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
`;

const SectionTitle = styled.div`
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    padding: 18px 0 10px 18px;
    color: rgba(0, 0, 0, 1);

    @media (max-width: 768px) {
        padding-left: 5px;
    }
`;

const MenuItem = styled.div<{
    $width?: string;
    $height?: string;
    $fontSize?: string;
    $padding?: string;
}>`
    width: ${(props) => props.$width || "172px"};
    height: ${(props) => props.$height || "39px"};
    font-size: ${(props) => props.$fontSize || "14px"};
    padding: ${(props) => props.$padding || "10px"};
    border-radius: 10px;
    color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: rgba(226, 229, 255, 1);
        color: rgba(0, 0, 0, 1);
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Footer = styled.div<{ $padding?: string; $gap?: string }>`
    display: flex;
    justify-content: flex-end;
    gap: ${(props) => props.$gap || "10px"};
    padding: ${(props) => props.$padding || "10px 16px"};
    border-top: 1px solid #f3f3f3;
`;

const ClearButton = styled.button`
    background: none;
    border: none;
    font-size: 13px;
    color: #8c8c8c;
    cursor: pointer;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
    background: rgba(103, 119, 255, 1);
    color: white;
    font-size: 13px;
    border: none;
    border-radius: 6px;
    padding: 10px 16px;
    cursor: pointer;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;
